import type { ProviderPage } from "@wags/catalog/listings/provider-page.contract.ts";
import { formatMoneyRange } from "@/lib/ui/price";
import type {
    Discount,
    FactCategory,
    Highlight,
    PricingFee,
    ProviderProfileV2,
} from "./types";

const FACT_CATEGORY: Record<
    ProviderPage["factGroups"][number]["category"],
    FactCategory
> = {
    care: "care",
    health_testing: "health_testing",
    behavior: "behavior",
    facility: "facility",
    contract: "contract",
    support: "support",
    logistics: "logistics",
    credential: "credential",
};

function operationsGroup(page: ProviderPage): ProviderProfileV2["factGroups"] {
    const screening = page.application?.screening ?? [];
    if (screening.length === 0) return [];
    return [
        {
            category: "operations",
            label: "How they screen",
            facts: screening.map((fact) => ({
                key: fact.key,
                label: fact.label,
                value: fact.value,
                note: fact.note,
            })),
        },
    ];
}

function pricingNotes(page: ProviderPage): string | undefined {
    const pricing = page.pricing;
    if (!pricing) return undefined;
    const priced = pricing.whatsIncluded.filter((item) => item.price != null);
    return (
        [
            pricing.summary,
            ...pricing.additionalFees,
            ...priced.map((item) => {
                const amount = formatMoneyRange(item.price);
                return amount ? `${item.name}: ${amount}` : item.name;
            }),
        ]
            .filter((value): value is string => Boolean(value))
            .join(" · ") || undefined
    );
}

function pricingFees(page: ProviderPage): PricingFee[] {
    const pricing = page.pricing;
    if (!pricing) return [];
    const range = pricing.priceRange;
    const label =
        page.provider.type === "breeder" ? "Puppy price" : "Adoption fee";

    const freeItems = pricing.whatsIncluded.filter(
        (item) => item.price == null,
    );
    const includes = freeItems.map((item) => item.name);
    const notes = pricingNotes(page);

    if (!range && includes.length === 0 && !notes) return [];

    return [
        {
            label,
            currency: range?.currency ?? "USD",
            min: range?.min ?? null,
            max: range?.max ?? null,

            typical: null,
            includes,
            notes: notes || undefined,
        },
    ];
}

function discounts(page: ProviderPage): Discount[] {
    if (!page.pricing) return [];
    return page.pricing.discounts.map((discount) => ({
        name: discount.name,
        amount: discount.amount?.min ?? null,
        currency: discount.amount?.currency ?? "USD",
        eligibility: discount.eligibility ?? undefined,
    }));
}

function highlights(page: ProviderPage): Highlight[] | undefined {
    if (page.provider.highlights.length === 0) return undefined;

    return page.provider.highlights.map((text) => ({ icon: "care", text }));
}

function profileIdentity(page: ProviderPage): ProviderProfileV2["identity"] {
    return {
        logoUrl: page.provider.logo?.url ?? null,
        websiteUrl: page.provider.websiteUrl,
        mission: page.provider.mission,
        certifications: page.provider.certifications,
        contactName: page.provider.contactName,
        applicationUrl: page.application?.url ?? null,
    };
}

function profileFactGroups(
    page: ProviderPage,
    screeningValues: Map<string, boolean | null>,
): ProviderProfileV2["factGroups"] {
    return [
        ...page.factGroups.map((group) => ({
            category: FACT_CATEGORY[group.category],
            label: group.label,
            facts: group.facts.map((fact) => ({
                key: fact.key,
                label: fact.label,

                value: screeningValues.get(fact.key) ?? null,
                note: fact.note,
            })),
        })),
        ...operationsGroup(page),
    ];
}

function profileReputation(
    page: ProviderPage,
): ProviderProfileV2["reputation"] {
    return {
        themes: {
            positives: page.reputation?.positives ?? [],
            negatives: page.reputation?.cautions ?? [],
        },
        reviewCount: page.reputation?.reviewCount ?? null,
        ratingCount: page.reputation?.ratingCount ?? null,
        averageRating: page.reputation?.averageRating ?? null,
    };
}

function profileOperations(
    page: ProviderPage,
): ProviderProfileV2["operations"] {
    if (!page.operations) return undefined;
    return {
        annualIntake: page.operations.annualIntake,
        annualAdoptions: page.operations.annualAdoptions,
        annualFosters: page.operations.annualFosters,
        onsiteVet: page.operations.onsiteVet,
        intakeModel: page.operations.intakeModel ?? null,
        acceptsOwnerSurrenders: page.operations.acceptsOwnerSurrenders ?? null,
        acceptsStrays: page.operations.acceptsStrays ?? null,
        strayHoldDays: page.operations.strayHoldDays ?? null,
        atRiskListPublished: page.operations.atRiskListPublished ?? null,
        mayEuthanizeForSpace: page.operations.mayEuthanizeForSpace ?? null,
    };
}

function profileTimeline(page: ProviderPage): ProviderProfileV2["timeline"] {
    const timeline = page.application?.timeline;
    if (!timeline) return undefined;
    return {
        summary: timeline.summary,
        sameDayPossible: timeline.sameDayPossible,
    };
}

export function providerPageToProfileV2(page: ProviderPage): ProviderProfileV2 {
    const screeningValues = new Map<string, boolean | null>(
        (page.application?.screening ?? []).map((fact) => [
            fact.key,
            fact.value,
        ]),
    );

    return {
        schemaVersion: "provider-profile.v1",
        providerId: page.provider.id,
        identity: profileIdentity(page),
        factGroups: profileFactGroups(page, screeningValues),
        highlights: highlights(page),
        availability: {
            listingCount: page.provider.listingCount,

            lastCheckedAt: null,

            upcomingLitters: undefined,
        },
        pricing: {
            fees: pricingFees(page),
            discounts: discounts(page),

            notes: pricingNotes(page),
        },
        reputation: profileReputation(page),
        operations: profileOperations(page),
        timeline: profileTimeline(page),
        programs: page.programs,
        restrictions: page.application?.restrictions,

        sources: [],
        lastProfileCheckedAt: null,
    };
}
